// In-memory ring buffer of recent OCR / classification outcomes.
//
// Companion to scanTelemetry. Where the telemetry buffer is optimised for a
// paste-into-bug-report flow (flush bodies, drop reasons, dex refetch
// counters), this store is optimised for an at-a-glance "what did the
// scanner just *see*" surface in the Live Scan / diagnostics UI.
//
// Each entry summarises one OCR pass: the raw and routed screen, OCR
// confidence, recognised identity (species/name/cp), accept/reject state,
// drop or warning reason, source kind (box/detail/catch/pokedex/journal),
// flush/batch outcome where known, and a sprite-cache verdict (observed
// crop captured/cached vs silhouette fallback).
//
// PRIVACY: never accepts raw dataUrl / base64 image bytes. The recorder
// only takes a boolean `spriteCaptured` so the UI can render the right
// indicator.

export type OcrResultStatus = "accepted" | "rejected" | "duplicate" | "empty";

export type OcrResultSource =
  | "box"
  | "detail"
  | "catch"
  | "pokedex"
  | "journal"
  | "unknown";

export interface OcrBoxRow {
  speciesId: number | null;
  name: string | null;
  cp: number | null;
  isShiny?: boolean | null;
  isLucky?: boolean | null;
  accepted: boolean;
  /** Set when accepted=false: dedupe, no_species, etc. */
  dropReason?: string | null;
}

export interface OcrResult {
  id: number;
  ts: number;
  /** Best-effort session correlation id; null if no session active. */
  sessionId: string | null;

  /** Raw screen from classifier. Always present. */
  rawScreen: string;
  /** Screen actually routed to (may differ if promoted from unknown). */
  routedScreen: string;
  routePromoted: boolean;
  routeReason: string | null;
  /** Classifier confidence (0-100 range, source-dependent). */
  classifierConfidence: number | null;
  /** OCR confidence reported by per-screen module (0-100). */
  ocrConfidence: number | null;

  source: OcrResultSource;
  /** What we ended up doing with this result. */
  status: OcrResultStatus;
  /** Reason a non-accepted result was rejected. */
  dropReason: string | null;
  warnings: string[];

  /** Recognised identity, when the route produced one. */
  speciesId: number | null;
  name: string | null;
  cp: number | null;

  /** For box/list scans: per-row preview. Sized small (cap at ~12 rows). */
  rows: OcrBoxRow[];

  /** Did we capture/cache an observed sprite for this result? */
  spriteCaptured: boolean;
  /** Was a silhouette fallback used because we have no observed crop? */
  spriteSilhouetteFallback: boolean;

  /**
   * Per-frame box metrics (only set when source === "box"). Sum of cells
   * we attempted plus the partition into accepted / duplicate / low-conf,
   * and the number of observed sprite crops captured this frame.
   */
  boxMetrics?: {
    rowsParsed: number;
    accepted: number;
    duplicates: number;
    lowConfidence: number;
    spritesCaptured: number;
  } | null;

  /** Last flush outcome attributable to this kind of result. */
  flushOutcome?: {
    endpoint: string;
    accepted: number | null;
    status: number | null;
  } | null;
}

export type OcrResultPatch = Partial<Omit<OcrResult, "id" | "ts">> & {
  spriteCaptured?: boolean;
  spriteSilhouetteFallback?: boolean;
};

export interface OcrResultStoreSnapshot {
  results: OcrResult[];
  totalRecorded: number;
}

const RING_SIZE = 25;
const MAX_BOX_ROWS = 12;

let ring: OcrResult[] = [];
let totalRecorded = 0;
let nextId = 1;
const listeners = new Set<() => void>();

function notify() {
  // Use Array.from so the loop works under tsconfig targets that don't
  // ship downlevelIteration for Set<T>. Push 2 follow-up (v1.4.1 b6):
  // resolves the only TS2802 outstanding on main.
  for (const fn of Array.from(listeners)) {
    try {
      fn();
    } catch {
      // never let one bad listener block the rest
    }
  }
}

function clampRows(rows: OcrBoxRow[] | undefined | null): OcrBoxRow[] {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const out: OcrBoxRow[] = [];
  for (const r of rows.slice(0, MAX_BOX_ROWS)) {
    out.push({
      speciesId:
        typeof r?.speciesId === "number" && Number.isFinite(r.speciesId)
          ? r.speciesId
          : null,
      name: typeof r?.name === "string" && r.name.length > 0 ? r.name : null,
      cp:
        typeof r?.cp === "number" && Number.isFinite(r.cp) ? r.cp : null,
      isShiny: !!r?.isShiny,
      accepted: !!r?.accepted,
      dropReason:
        typeof r?.dropReason === "string" && r.dropReason.length > 0
          ? r.dropReason
          : null,
    });
  }
  return out;
}

function normalise(patch: OcrResultPatch): OcrResult {
  return {
    id: nextId++,
    ts: Date.now(),
    sessionId:
      typeof patch.sessionId === "string" && patch.sessionId.length > 0
        ? patch.sessionId
        : null,
    rawScreen: typeof patch.rawScreen === "string" ? patch.rawScreen : "unknown",
    routedScreen:
      typeof patch.routedScreen === "string" ? patch.routedScreen : "unknown",
    routePromoted: !!patch.routePromoted,
    routeReason:
      typeof patch.routeReason === "string" && patch.routeReason.length > 0
        ? patch.routeReason
        : null,
    classifierConfidence:
      typeof patch.classifierConfidence === "number"
        && Number.isFinite(patch.classifierConfidence)
        ? patch.classifierConfidence
        : null,
    ocrConfidence:
      typeof patch.ocrConfidence === "number"
        && Number.isFinite(patch.ocrConfidence)
        ? patch.ocrConfidence
        : null,
    source: (patch.source ?? "unknown") as OcrResultSource,
    status: (patch.status ?? "rejected") as OcrResultStatus,
    dropReason:
      typeof patch.dropReason === "string" && patch.dropReason.length > 0
        ? patch.dropReason
        : null,
    warnings: Array.isArray(patch.warnings)
      ? patch.warnings.filter((w) => typeof w === "string").slice(0, 6)
      : [],
    speciesId:
      typeof patch.speciesId === "number" && Number.isFinite(patch.speciesId)
        ? patch.speciesId
        : null,
    name:
      typeof patch.name === "string" && patch.name.length > 0
        ? patch.name
        : null,
    cp:
      typeof patch.cp === "number" && Number.isFinite(patch.cp) ? patch.cp : null,
    rows: clampRows(patch.rows),
    spriteCaptured: !!patch.spriteCaptured,
    spriteSilhouetteFallback: !!patch.spriteSilhouetteFallback,
    boxMetrics:
      patch.boxMetrics && typeof patch.boxMetrics === "object"
        ? {
            rowsParsed: clampInt(patch.boxMetrics.rowsParsed),
            accepted: clampInt(patch.boxMetrics.accepted),
            duplicates: clampInt(patch.boxMetrics.duplicates),
            lowConfidence: clampInt(patch.boxMetrics.lowConfidence),
            spritesCaptured: clampInt(patch.boxMetrics.spritesCaptured),
          }
        : null,
    flushOutcome: patch.flushOutcome ?? null,
  };
}

function clampInt(v: unknown): number {
  if (typeof v !== "number" || !Number.isFinite(v) || v < 0) return 0;
  return Math.min(999, Math.floor(v));
}

export function recordOcrResult(patch: OcrResultPatch): OcrResult {
  const entry = normalise(patch);
  ring.push(entry);
  if (ring.length > RING_SIZE) {
    ring.splice(0, ring.length - RING_SIZE);
  }
  totalRecorded += 1;
  notify();
  return entry;
}

/**
 * Attach the most recent flush outcome to any results that look like they
 * fed into it (by source). Pure best-effort; we walk the tail of the ring
 * and back-fill rather than re-record. Used by the LiveScan flush hooks.
 */
export function annotateLastFlushOutcome(
  source: OcrResultSource,
  outcome: { endpoint: string; accepted: number | null; status: number | null },
): void {
  let changed = false;
  for (let i = ring.length - 1; i >= 0; i--) {
    const r = ring[i];
    if (r.source !== source) continue;
    if (r.flushOutcome) break; // already annotated; stop walking
    r.flushOutcome = outcome;
    changed = true;
    if (i < ring.length - 6) break; // don't annotate ancient rows
  }
  if (changed) notify();
}

export function getOcrResultSnapshot(): OcrResultStoreSnapshot {
  return { results: ring.slice(), totalRecorded };
}

export function subscribeOcrResults(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export function resetOcrResults(): void {
  ring = [];
  totalRecorded = 0;
  nextId = 1;
  notify();
}

export const __testing = {
  ringSize: RING_SIZE,
  maxBoxRows: MAX_BOX_ROWS,
};
